import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import LockIcon from '@mui/icons-material/LockOutlined';
import SearchOffIcon from '@mui/icons-material/SearchOffOutlined';
import { useLocalization } from '@/hooks/useLocalization';

export type ClientAccessErrorVariant = 'forbidden' | 'notFound';

interface ClientAccessErrorPanelProps {
  variant: ClientAccessErrorVariant;
  onBack?: () => void;
  backLabel?: string;
}

const ClientAccessErrorPanel: React.FC<ClientAccessErrorPanelProps> = ({
  variant,
  onBack,
  backLabel,
}) => {
  const { t } = useLocalization();

  const content = {
    forbidden: {
      title: t('clientAccessForbiddenTitle', 'Access denied'),
      message: t(
        'clientAccessForbiddenMessage',
        "You don't have access to this client.",
      ),
      Icon: LockIcon,
    },
    notFound: {
      title: t('clientAccessNotFoundTitle', 'Client not found'),
      message: t(
        'clientAccessNotFoundMessage',
        'This client does not exist or is no longer available.',
      ),
      Icon: SearchOffIcon,
    },
  }[variant];

  const { title, message, Icon } = content;

  return (
    <Box
      display='flex'
      flexDirection='column'
      alignItems='center'
      justifyContent='center'
      gap={1.5}
      py={6}
      px={3}
    >
      <Icon
        color='action'
        sx={{ fontSize: 48 }}
      />
      <Typography variant='h6'>{title}</Typography>
      <Typography
        variant='body2'
        color='text.secondary'
        textAlign='center'
      >
        {message}
      </Typography>
      {onBack && (
        <Button
          variant='outlined'
          size='small'
          onClick={onBack}
          sx={{ mt: 1 }}
        >
          {backLabel ?? t('backToClients', 'Back to Clients')}
        </Button>
      )}
    </Box>
  );
};

export default ClientAccessErrorPanel;
